"use client";

// Day/night indicator + countdown that sits between the server pill and
// the account menu in the site header.
//
// Subscribes to /api/sse/group, filters server.time for the user's
// current paired server, and re-renders the countdown every second by
// interpolating the cached AppTime forward locally — the worker only
// re-fetches the clock every 30s, so without local interpolation the
// countdown would step in 30s chunks instead of ticking smoothly.
//
// "Day" or "Night" is implicit in the icon (sun vs moon); the text
// shows what's coming next ("Night in 4m 23s" when it's currently day).

import { useEffect, useReducer, useState } from "react";
import { useSSE } from "@/hooks/useSSE";
import {
  type AppTimeSnapshot,
  formatCountdown,
  isDayAt,
  interpolateGameTime,
  nextTransition,
} from "@/lib/time/day-night";

interface State {
  time: AppTimeSnapshot | null;
}

type Action = { type: "time"; snap: AppTimeSnapshot };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "time":
      return { time: action.snap };
    default:
      return state;
  }
}

// `variant` controls where this renders:
//   "full"    — the desktop pill (right zone); hidden under 640px.
//   "compact" — a smaller pill shown ONLY on mobile, sitting next to the
//               avatar in the left zone (the full pill's slot is gone on
//               mobile). Visibility for both is driven by CSS in
//               site-header.tsx (class-based media queries), mirroring how
//               the other header bits fold.
export function HeaderDayNight({
  serverKey,
  variant = "full",
}: {
  serverKey: string;
  variant?: "full" | "compact";
}) {
  const [state, dispatch] = useReducer(reducer, { time: null });
  // Forces a re-render once per second so the interpolated countdown
  // ticks. The actual snapshot only updates every 30s; this is just
  // the visual heartbeat.
  const [, tick] = useState(0);

  useSSE("/api/sse/group", (event) => {
    if (event.type !== "server.time") return;
    if (event.payload.serverKey !== serverKey) return;
    const snap = event.payload.time;
    if (!snap || typeof snap !== "object") return;
    // Rebase capturedAtMs to the browser's clock at receive time. Why:
    // the worker stamps capturedAtMs with ITS Date.now() at fetch. If
    // the user's device clock is off by N minutes from the worker's
    // (NTP-synced) clock, the browser's interpolation uses the wrong
    // frame and the countdown drifts by N minutes — diverging from the
    // worker's !time chat reply, which uses the worker's own clock.
    // Rebasing collapses both onto the browser's local clock; cost is
    // ~100ms of accuracy (network transit + worker processing delay
    // between snapshot capture and receive), which is invisible
    // compared to "your countdown is off by 5 minutes."
    const rebased = { ...(snap as AppTimeSnapshot), capturedAtMs: Date.now() };
    dispatch({ type: "time", snap: rebased });
  });

  useEffect(() => {
    if (!state.time) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [state.time]);

  if (!state.time) return null;

  const now = Date.now();
  const gameHour = interpolateGameTime(state.time, now);
  const day = isDayAt(gameHour, state.time.sunrise, state.time.sunset);
  const next = nextTransition(state.time, now);
  const nextLabel = next.kind === "until_day" ? "Day" : "Night";
  const countdown = formatCountdown(next.realSeconds);
  const compact = variant === "compact";

  return (
    <span
      className={compact ? "header-day-night-mobile" : "header-day-night"}
      title={`In-game ${formatGameClock(gameHour)} · ${nextLabel} in ${countdown}`}
      style={{
        // Compact starts hidden (display:none) and is revealed only inside
        // the mobile media query; the full pill is the inverse. CSS
        // !important overrides these inline values — same trick the rest
        // of the header uses to fold.
        display: compact ? "none" : "inline-flex",
        alignItems: "center",
        gap: compact ? "0.3rem" : "0.4rem",
        padding: compact ? "0.2rem 0.4rem" : "0.3rem 0.55rem",
        background: "var(--bg)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        fontSize: compact ? "0.72rem" : "0.78rem",
        color: "var(--fg)",
        lineHeight: 1.1,
        whiteSpace: "nowrap",
      }}
    >
      {day ? <SunIcon size={compact ? 14 : 16} /> : <MoonIcon size={compact ? 14 : 16} />}
      <span style={{ color: "var(--fg-muted)" }}>{nextLabel} in</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{countdown}</span>
    </span>
  );
}

// Format a 0..24 game hour into HH:MM for the tooltip only.
function formatGameClock(hourFloat: number): string {
  const hours = Math.floor(hourFloat) % 24;
  const minutes = Math.floor((hourFloat - Math.floor(hourFloat)) * 60);
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

// Material-style sun icon, single-color via currentColor so we can
// tint with CSS (currently uses the accent color in day mode).
function SunIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      style={{ color: "var(--accent)" }}
    >
      <path
        fill="currentColor"
        d="M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm0-5a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1Zm0 17a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1ZM4.22 4.22a1 1 0 0 1 1.42 0l1.42 1.42a1 1 0 1 1-1.42 1.42L4.22 5.64a1 1 0 0 1 0-1.42Zm12.72 12.72a1 1 0 0 1 1.42 0l1.42 1.42a1 1 0 1 1-1.42 1.42l-1.42-1.42a1 1 0 0 1 0-1.42ZM2 12a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Zm17 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1ZM4.22 19.78a1 1 0 0 1 0-1.42l1.42-1.42a1 1 0 1 1 1.42 1.42L5.64 19.78a1 1 0 0 1-1.42 0Zm12.72-12.72a1 1 0 0 1 0-1.42l1.42-1.42a1 1 0 1 1 1.42 1.42l-1.42 1.42a1 1 0 0 1-1.42 0Z"
      />
    </svg>
  );
}

// Material-style moon icon (crescent), tinted muted-blue to read as
// night without fighting the accent color for attention.
function MoonIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      style={{ color: "#9db4d6" }}
    >
      <path
        fill="currentColor"
        d="M21.64 13a1 1 0 0 0-1.05-.14 8 8 0 0 1-3.37.73 8.15 8.15 0 0 1-8.14-8.1 8.59 8.59 0 0 1 .25-2A1 1 0 0 0 8 2.36a10.14 10.14 0 1 0 14 11.69 1 1 0 0 0-.36-1.05Z"
      />
    </svg>
  );
}
